// In-memory per-user session store. Survives logout → login within a single
// page load, so a returning user gets their history back; a full page reload
// re-evaluates this module and clears it (session-scoped, matching the demo's
// "resets on reload" model). Keyed by user_id.
//
// Approvals are NOT here — they're a shared governance queue (see approvalsStore),
// because a request one user raises may be actioned by a different (admin) user.

const store = new Map()

export function emptySession() {
  return {
    chatMessages: [],
    notifs: [],
    liveActivity: [],
    metrics: { questions: 0, tables: 0, sources: 0 },
    sessionId: null,
    // Per-agent chat threads for the UserWorkspace. threads[agentName] =
    // { messages: [], sessionId }. Kept separate from the admin `chatMessages`
    // field (the Dashboard still reads that) so both shells coexist.
    threads: {},
  }
}

// A stable id per (user, agent) thread. crypto.randomUUID exists in browsers and
// modern Node; the fallback keeps tests running on older runtimes.
function newSessionId(agentName) {
  const uuid =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`
  return `ws-${agentName}-${uuid}`
}

// Lazily get (and create) the thread for an agent. Mutates `session.threads` so
// the caller can persist the whole session afterwards. The sessionId is stable:
// created once and reused for every message in that thread, which is how the
// backend accumulates conversation history keyed by (sessionId, agent).
export function getThread(session, agentName) {
  if (!session.threads) session.threads = {}
  if (!session.threads[agentName]) {
    session.threads[agentName] = { messages: [], sessionId: newSessionId(agentName) }
  }
  return session.threads[agentName]
}

// This user's saved session, or a fresh empty one. Unknown/absent id -> empty.
export function loadSession(userId) {
  if (!userId) return emptySession()
  return store.get(userId) || emptySession()
}

// Persist this user's session. No-op without a user id.
export function saveSession(userId, session) {
  if (!userId) return
  store.set(userId, session)
}

// Cross-user delivery: append a chat message and/or a notification to another
// user's stored session (used when an admin approves a request that a different
// user raised — the answer lands in the requester's chat for when they return).
export function appendToUserSession(userId, { message = null, notif = null } = {}) {
  if (!userId) return
  const s = loadSession(userId)
  saveSession(userId, {
    ...s,
    chatMessages: message ? [...s.chatMessages, message] : s.chatMessages,
    notifs: notif ? [notif, ...s.notifs] : s.notifs,
  })
}
